import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';

export interface IndividualClientData {
  id: string;
  name: string;
  mobile?: string;
  address?: string;
  dateOfRegistration?: Date | any;
  locationId?: string;
  securityCode?: string;
  requiredGuard?: number;
  contactName?: string;
  contactEmail?: string;
  contactNumber?: string;
  /** @deprecated For individual clients use guardSalaries (per-guard) instead. Kept for backward compatibility. */
  salary?: number;
  /** Per-guard monthly salary (guardId -> amount). Used for attendance/salary calculation. */
  guardSalaries?: Record<string, number>;
}

export interface LocationData {
  id: string;
  name: string;
}

export const fetchLocations = async (): Promise<LocationData[]> => {
  try {
    const locationsSnap = await getDocs(collection(db, 'locations'));
    return locationsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as LocationData[];
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
};

export const fetchIndividualClients = async (companyId?: string): Promise<IndividualClientData[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  const clientsSnap = await getDocs(
    collection(db, 'Individual', uid, 'clients')
  );

  return clientsSnap.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      contactName: data.contactName || '',
      contactEmail: data.contactEmail || '',
      contactNumber: data.contactNumber || '',
      salary: data.salary || 0,
      dateOfRegistration: data.dateOfRegistration?.toDate
        ? data.dateOfRegistration.toDate()
        : data.dateOfRegistration || null,
    } as IndividualClientData;
  });
};

export const updateIndividualClient = async (
  clientId: string,
  data: Partial<IndividualClientData>,
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  const clientRef = doc(db, 'Individual', uid, 'clients', clientId);

  const updateData: any = {
    name: data.name,
    mobile: data.mobile || null,
    address: data.address || null,
    locationId: data.locationId || null,
    requiredGuard: data.requiredGuard || 1,
    contactName: data.contactName || null,
    contactEmail: data.contactEmail || null,
    contactNumber: data.contactNumber || null,
  };

  if (data.dateOfRegistration) {
    updateData.dateOfRegistration =
      data.dateOfRegistration instanceof Date
        ? data.dateOfRegistration
        : data.dateOfRegistration?.toDate
          ? data.dateOfRegistration.toDate()
          : data.dateOfRegistration;
  }
  if (data.guardSalaries !== undefined) updateData.guardSalaries = data.guardSalaries;
  if (data.salary !== undefined) updateData.salary = data.salary;

  await updateDoc(clientRef, updateData);
};

export const createIndividualClient = async (
  clientName: string,
  mobile: string,
  address: string,
  locationId: string,
  dateOfRegistration: Date | null,
  companyId: string,
  requiredGuard: number = 1,
  contactName: string = '',
  contactEmail: string = '',
  contactNumber: string = '',
  salary: number = 0
): Promise<void> => {
  if (!companyId) throw new Error('Company ID is required');
  if (!clientName.trim()) throw new Error('Client name is required');

  // Generate security code
  const firstThree = clientName.trim().substring(0, 3).toUpperCase();
  const randomSix = Math.floor(100000 + Math.random() * 900000);
  const securityCode = `${firstThree}-${randomSix}`;

  // Create client document
  const clientRef = doc(collection(db, 'Individual', companyId, 'clients'));
  await setDoc(clientRef, {
    name: clientName.trim(),
    mobile: mobile.trim() || null,
    address: address.trim() || null,
    locationId: locationId || null,
    dateOfRegistration: dateOfRegistration || null,
    requiredGuard: requiredGuard || 1,
    contactName: contactName.trim() || null,
    contactEmail: contactEmail.trim() || null,
    contactNumber: contactNumber.trim() || null,
    salary: salary || 0,
    securityCode,
    type: 'Individual',
    companyId,
    createdAt: new Date(),
  });

  // Save security code reference
  const securityCodeRef = doc(collection(db, 'securityCodes'));
  await setDoc(securityCodeRef, {
    companyId,
    clientType: 'Individual',
    clientId: clientRef.id,
    securityCode,
  });
};

export const formatDate = (dateObj: any): string => {
  if (!dateObj) return 'N/A';
  if (typeof dateObj.toDate === 'function') {
    return dateObj.toDate().toLocaleDateString();
  }
  if (typeof dateObj === 'string') {
    const date = new Date(dateObj);
    return isNaN(date.getTime()) ? 'Invalid' : date.toLocaleDateString();
  }
  if (dateObj instanceof Date) {
    return dateObj.toLocaleDateString();
  }
  return 'N/A';
};
